import React, { useState, useContext, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import Context from "./EventsProvider";

//style rules
import { Colors, Typography, Sizes } from "../../styles";

const DropDown = ({ DropDownLabel, dropDownFilters, filterKey }) => {
	const {
		actions: {setParams, setLabel,},
		data: {language, label, labelText},
	} = useContext(Context);

	const [openFiltersDropDown, setOpenFiltersDropDown] = useState(false);

	const dropDownNode = useRef();

	const clearFilterLabel = () => {
		if (language === "en"){
			return `All ${DropDownLabel.toLowerCase()}s`;
		}
		return `Tous les ${DropDownLabel.toLowerCase()}x`;
	}

	const handleOutsideClick = (e) => {
		if (dropDownNode.current && !dropDownNode.current.contains(e.target)) {
			toggleDropDown();
		}
	};

	useEffect(() => {
		if (openFiltersDropDown) {
			document.addEventListener("mousedown", handleOutsideClick);
		}
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, [openFiltersDropDown]);

	const toggleDropDown = () => {
		setOpenFiltersDropDown((currentValue) => !currentValue);
	};

	const handleFiltersSelect = (filterObject, filterTitle) => {
		const resetFilters = filterObject.location === 'all';
		const label = filterObject.location === 'dropdown_label';

		if (label) {	
			toggleDropDown();
			return;
		}

		if (resetFilters) {
			setParams((currentValue) => {
				if (!currentValue.location) return currentValue;
				const paramsCopy = { ...currentValue };
				delete paramsCopy.location;
				return paramsCopy;
			});

			setLabel( labelText() );
		} else {
			setParams((currentValue) => {
				return { ...currentValue, ...filterObject };
			});
			
			setLabel(filterTitle);
		}
		
		toggleDropDown();
	}

	const handleKeyUp = (event) => {
		if (event.key === "Enter") {
			toggleDropDown();
		}
	};

	const renderFilters = () => {
		// we only run the function when the dropdown is triggered
		if (!openFiltersDropDown) return;

		const newDropdownFilters = [
			{slug: 'dropdown_label', name: labelText()},
			{slug: 'all', name: clearFilterLabel()},  
			...dropDownFilters]

		return (
			<DropDownList 
				aria-hidden={!openFiltersDropDown}
				role="listbox"
				id="dropdown-list"
			>
				{newDropdownFilters &&
					newDropdownFilters.length > 0 &&
					newDropdownFilters.map((filter) => {
						return (
							<DropDownListItem
								key={filter.slug}
								onClick={() => {
									handleFiltersSelect({[filterKey]: filter.slug}, filter.name);
								}}
								tabIndex='0'
								className={filter.slug === 'dropdown_label' ? 'dropdown-label' : null}
								role="option"
								aria-selected={label === filter.name}
							>
								<ItemLabel
									dangerouslySetInnerHTML={{ __html: filter.name }}
								/>
							</DropDownListItem>
						);
					})}
			</DropDownList>
		);
	};

	return (
		<DropDownContainer 
			ref={dropDownNode} 
			aria-expanded={!openFiltersDropDown}
			aria-controls="dropdown-list"
			className={openFiltersDropDown ? 'open' : null}
		>
			<DropDownTrigger onClick={toggleDropDown} onKeyUp={handleKeyUp}>
				{label}
			</DropDownTrigger>
			{renderFilters()}
		</DropDownContainer>
	);
};

export default DropDown;

DropDown.propTypes = {
	DropDownLabel: PropTypes.string,
	dropDownFilters: PropTypes.array,
	filterKey: PropTypes.string,
};

const DropDownTrigger = styled.button`
    width: 100%;
	padding: 1.5rem 5rem 1.5rem 3rem;
	border: 2px solid ${Colors.fp.pineGreen};
	border-radius: 4rem;
	font-size: 1.5rem;
	line-height: 1.9rem;
    font-weight: ${Typography.fontWeight.medium};
	cursor: pointer;
	text-align: left;
	height: unset;
	background-color: ${Colors.fp.pineGreen10};
    text-transform: unset;
	color: ${Colors.grey.charcoal};

	&:focus-visible {
		outline: solid 2px -webkit-focus-ring-color;
		outline: solid 2px Highlight;
	}

	&:hover,
	&:focus {
		padding-right: 5rem;
	}
    
	&::after {
        content: url(${mainScript.assetPath + "images/svg/icons/down-arrow-black.svg"}) !important;
		opacity: 1 !important;
		visibility: visible !important;
		right: 3rem;
		padding: unset !important;
		top: 35% !important;
		transform: unset !important;
		transition: unset !important;
		position: absolute;
    }
`;

const DropDownContainer = styled.div`
    width: 100%;
	position: relative;
    margin: 0;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		flex-basis: 32.8rem;
	}

	&.open {
		& > ${DropDownTrigger} {
			&::after {
				transform: rotate(180deg) !important;
				top: 1.5rem !important;
			}
		}

		.dropdown-label {
			&::after {
				transform: rotate(180deg);
			}
		}
	}
`;
    
const DropDownList = styled.ul`
	width: 100%;
	max-height: 42rem;
	overflow-y: scroll;
	display: flex;
	flex-direction: column;
	margin: 0;
	padding: 0.75rem 0 1.25rem 0;
	position: absolute;
	top: 0;
	z-index: 3;
	background-color: ${Colors.fp.pineGreen10};
	border: 2px solid ${Colors.fp.pineGreen};
	border-top-left-radius: 2.6rem;
    border-top-right-radius: 2.6rem;
    border-bottom-left-radius: 4rem;
    border-bottom-right-radius: 4rem;
	scrollbar-width: none;
`;

const ItemLabel = styled.label`
	font-size: 1.7rem !important;
	line-height: 2.4rem;
	font-weight: ${Typography.fontWeight.medium};
	cursor: pointer;
	text-transform: unset;
	letter-spacing: normal;
	padding: 0 3rem;
`;

const DropDownListItem = styled.li`
	margin: 0;
	padding: 0.75rem 0;
	list-style: none;
	cursor: pointer;

	&:hover,
	&:focus-visible {
		background-color: ${Colors.fp.pineGreen40};
	}

	label::first-letter {
		text-transform: uppercase;
	}

	&.dropdown-label ${ItemLabel} {
		pointer-events: none;
		cursor: not-allowed;
		font-size: 1.5rem !important;
		line-height: 1.9rem;
		font-weight: ${Typography.fontWeight.medium};
		color: ${Colors.grey.charcoal};
		position: relative;
	}

	&.dropdown-label::after {
		content: url(${mainScript.assetPath + "images/svg/icons/down-arrow-black.svg"});
		right: 3rem;
    	top: 1.5rem;
		position: absolute;
	}	
`;